import React, { useEffect } from 'react'
import { GTM_CONTENTSQUARE_TRIGGER } from 'utils/tracking'
import { csIds } from 'utils/tracking'

const withCSTracker = (Component) => {
    return (props) => {
        const csCategoryMap = buildCategoryMap(csIds)
        let lastClick = { id: null, time: 0 }

        useEffect(() => {
            document.addEventListener('click', csHandler)

            return () => document.removeEventListener('click', csHandler, true)
        }, [])

        const csHandler = (e) => {
            try {
                const el = e.target.closest && e.target.closest('[data-cs-id]')
                if (!el) return

                const csId = el.getAttribute('data-cs-id')
                if (!csId) return

                const now = Date.now()
                if (lastClick.id === csId && now - lastClick.time < 300) return
                lastClick = { id: csId, time: now }

                // Determine category: priority -> data-cs-category -> config -> fallback
                const explicitCategory = el.getAttribute('data-cs-category')
                if (explicitCategory === 'modal') return
                const inferredCategory = csCategoryMap[csId] || 'UI'
                const category = explicitCategory || inferredCategory

                // Build content (smart fallback)
                const explicitContent = el.getAttribute('data-cs-content')
                const text = extractReadableText(el)
                const label =
                    el.getAttribute('aria-label') ||
                    el.getAttribute('alt') ||
                    el.getAttribute('title') ||
                    ''
                const content = explicitContent || text || label || ''

                const eventData = {
                    name: csId,
                    category,
                    metadata: {
                        ...(content ? { content } : {}),
                        tag: el.tagName,
                        path: safeGetElementPath(el),
                    },
                }

                window.dataLayer.push({
                    event: GTM_CONTENTSQUARE_TRIGGER,
                    cs_id: csId,
                    cs_category: category,
                    cs_content: content,
                })

                safeLogTrackedEvent(eventData)
            } catch (err) {
                console.warn('[CS Tracker] Failed to track event:', err)
            }
        }

        return <Component {...props} />
    }
}

const buildCategoryMap = (csIds) => {
    try {
        const map = {}
        Object.entries(csIds).forEach(([category, items]) => {
            Object.values(items).forEach((id) => {
                map[id] = category
            })
        })
        return map
    } catch (err) {
        console.log('[Contentsquare] Failed to build category map:', err)
        return {}
    }
}

const extractReadableText = (el) => {
    try {
        if (!el) return ''

        // TreeWalker iterates text nodes only
        const walker = document.createTreeWalker(
            el,
            NodeFilter.SHOW_TEXT,
            null,
            false
        )

        const parts = []

        // Collect trimmed text nodes
        while (walker.nextNode()) {
            const text = walker.currentNode.nodeValue.trim()

            // Ignore empty nodes (like formatting spaces, line breaks)
            if (text) {
                parts.push(text)
            }
        }

        // Join with "·" to avoid glued text
        const combined = parts
            .join(' · ')
            .replace(/\s+/g, ' ') // normalize whitespace
            .trim()

        // Limit length for Contentsquare metadata safety (100 chars recommended)
        if (combined) {
            return combined.slice(0, 100)
        }
    } catch (err) {
        /**
         * We catch EVERYTHING here because:
         * - analytics must never throw UI runtime errors
         * - if DOM parsing fails, we fall back and move on
         */
        console.warn('[CS Tracker] extractReadableText failed:', err)
    }

    // Guaranteed fallback: safe and consistent with old behavior
    const text = el.textContent || ''
    return text
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 100)
}

/**
 * Safely builds a CSS path
 */
export const safeGetElementPath = (el) => {
    try {
        if (!el || el.nodeType !== 1) return ''
        const path = []
        while (el && el.nodeType === 1 && el !== document.body) {
            let selector = el.tagName.toLowerCase()
            if (el.id) selector += `#${el.id}`
            else if (el.className) {
                const cls = el.className
                    .toString()
                    .trim()
                    .split(/\s+/)[0]
                if (cls) selector += `.${cls}`
            }
            path.unshift(selector)
            el = el.parentElement
        }
        return path.join(' > ')
    } catch (err) {
        return ''
    }
}

/**
 * Safe console logger
 */
export const safeLogTrackedEvent = (eventData) => {
    try {
        const { name, category, metadata } = eventData
        const message = `%c[Contentsquare]%c Tracked "%c${name}%c" (${category})`
        const style1 =
            'color: #fff; background: #0070f3; padding: 2px 6px; border-radius: 3px;'
        const style2 = 'color: #999;'
        const style3 = 'color: #00b341; font-weight: bold;'
        const style4 = 'color: #999;'
        // console.log(message, style1, style2, style3, style4)
        // console.log('Metadata:', metadata)
    } catch (err) {
        // even console log errors are ignored
        console.log('Failed to log contentsquare data')
    }
}

export default withCSTracker
